/*
 * This file is part of Invenio.
 * Copyright (C) 2016-2021 CERN.
 * Copyright (C) 2023 Northwestern University.
 *
 * Invenio is free software; you can redistribute it and/or modify it
 * under the terms of the MIT License; see LICENSE file for more details.
 */

import _truncate from "lodash/truncate";
import React, { Component } from "react";
import { Image, withCancel } from "react-invenio-forms";
import { Button, Card, Container, Grid, Message, Placeholder } from "semantic-ui-react";

import PropTypes from "prop-types";
import { http } from "react-invenio-forms";
import { CommunityType } from "@js/invenio_communities";

const PlaceholderLoader = ({ size, isLoading, children, ...props }) => {
    const PlaceholderItem = () => (
        <Grid.Column width={3}>
            <Placeholder>
                <Placeholder.Image square />
            </Placeholder>
            <Placeholder>
                <Placeholder.Paragraph>
                    <Placeholder.Line length="medium" />
                    <Placeholder.Line length="short" />
                </Placeholder.Paragraph>
            </Placeholder>
        </Grid.Column>
    );
    let numberOfHeader = [];
    for (let i = 0; i < size; i++) {
        numberOfHeader.push(<PlaceholderItem key={i} />);
    }

    if (!isLoading) {
        return children;
    }
    return (
        <Grid columns="equal" stackable>
            {numberOfHeader}
        </Grid>
    );
};

PlaceholderLoader.propTypes = {
    size: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
};

PlaceholderLoader.defaultProps = {
    size: 5,
};

const EmptyMessage = ({ message }) => {
    return <Message icon="info" header={message} />;
};

EmptyMessage.propTypes = {
    message: PropTypes.string.isRequired,
};

class CommunityCard extends Component {
    render() {
        const { community, defaultLogo, type } = this.props;
        let resultHref = community.links.self_html;

        return (
            <Card fluid href={resultHref}>
                <Image wrapped centered ui={false} src={community.links.logo} fallbackSrc={defaultLogo} loadFallbackFirst />
                <Card.Content>
                    <Card.Header>{_truncate(community.metadata.title, { length: 30 })}</Card.Header>
                    {community.metadata.description && (
                        <Card.Description>
                            <div className="truncate-lines-2">{community.metadata.description}</div>
                        </Card.Description>
                    )}
                </Card.Content>
            </Card>
        );
    }
}

CommunityCard.propTypes = {
    community: PropTypes.object.isRequired,
    defaultLogo: PropTypes.string.isRequired,
};

class CommunitiesCardGroup extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            data: { hits: [] },
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    componentWillUnmount() {
        this.cancellableFetch && this.cancellableFetch.cancel();
    }

    fetchData = async () => {
        const { fetchDataUrl } = this.props;
        this.setState({ isLoading: true });
        this.cancellableFetch = withCancel(http.get(fetchDataUrl));

        try {
            const response = await this.cancellableFetch.promise;

            this.setState({ data: response.data.hits, isLoading: false });
        } catch (error) {
            // TODO: handle error response
        }
    };

    renderCards(typePlural) {
        const { data } = this.state;
        const { defaultLogo } = this.props;

        return data.hits.map((community) => {
            return <CommunityCard key={community.id} community={community} type={typePlural} defaultLogo={defaultLogo} />;
        });
    }

    render() {
        const { isLoading, data } = this.state;
        const { emptyMessage, itemsPerRow, communityType } = this.props;
        let communityTypeObj = new CommunityType(communityType);
        let typePlural = communityTypeObj.getPlural();
        let typeApi = communityTypeObj.getApi();
        return (
            <PlaceholderLoader isLoading={isLoading}>
                {data.hits.length > 0 ? (
                    <div className="ui raised very container segment">
                        <h2 className="ui header">{communityTypeObj.getPluralCapitalized()}</h2>
                        <Card.Group doubling stackable itemsPerRow={itemsPerRow ? itemsPerRow : 5} className="community-frontpage-cards">
                            {this.renderCards(typePlural)}
                        </Card.Group>
                        <Container textAlign="center" className="ui rel-mt-2">
                            <Button href={`/${typeApi}/search?q=${this.props.searchquery}`}>View all matching {typePlural}</Button>
                        </Container>
                    </div>
                ) : (
                    <div className="ui raised very container segment">
                        <h2 className="ui header">{emptyMessage}</h2>
                        <Container textAlign="center" className="ui rel-mt-2">
                            <Button href={`/${typeApi}/search`}>View all {typePlural}</Button>
                        </Container>
                    </div>
                )}
            </PlaceholderLoader>
        );
    }
}

CommunitiesCardGroup.propTypes = {
    fetchDataUrl: PropTypes.string.isRequired,
    defaultLogo: PropTypes.string.isRequired,
    emptyMessage: PropTypes.string.isRequired,
    itemsPerRow: PropTypes.number,
    communityType: PropTypes.string.isRequired,
};

export default CommunitiesCardGroup;
